/**
 * PlaylistSearchTileV3 — Cover-Flow Cinematic.
 *
 * Hero composition: a 3-D Cover-Flow style carousel inspired by Apple
 * Music / iTunes Cover Flow. Cards are arranged on an arc, the
 * centermost facing the camera (rotateY 0deg), neighbors rotated
 * -30deg / +30deg, outer cards -60deg / +60deg. The "front" position
 * auto-cycles every 3 s, rotating the set so each thumbnail takes a
 * turn at the center.
 *
 * Implementation strategy (CSS-only, no JS timers):
 *   • Each card runs the same `playlist-search-tile-v3-coverflow-${N}`
 *     keyframe (N = total thumbnail count). The keyframe steps through
 *     N slot positions over `N * 3s`, snapping with `step-end` at each
 *     boundary so transitions between slots are continuous (the next
 *     card slides forward as the previous one slides aside).
 *   • Each card receives `animation-delay: -(i * 3s)` so at any instant
 *     each card occupies a different slot — the set as a whole rotates.
 *   • Keyframe sets are pre-generated in SCSS for N = 1..8. For N > 8
 *     we fall back to the 8-card keyframe (extra cards still cycle
 *     through the visible slots in turn).
 *
 * Variant comparison:
 *   • A `variantIndex` prop (1..10) selects between 10 cover-flow
 *     animation styles for side-by-side comparison. Variant 1 is the
 *     baseline (current behavior). Variants 4 / 5 change the visible
 *     card count (7 / 3 respectively); the rest are CSS-only overrides
 *     of rotation depth, cycle duration, or animation triggers.
 *
 * Edge cases:
 *   • Empty array     → static <StackedCards /> placeholder, no motion.
 *   • Single thumb    → centered card, scale 1.0, no rotation, no motion.
 *   • Two thumbs      → alternates front/back every 3s with simple flip.
 *   • Reduced motion  → pin front-index to the middle thumbnail; all
 *                       cards still arranged on the arc, no auto-cycle.
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'
import type { PlaylistSearchTileProps } from '../../shared/types'
import PlaylistTileShell from '../../shared/PlaylistTileShell'
import PlaylistTileHoverBodyV3 from './PlaylistTileHoverBodyV3'

const MAX_KEYFRAME_N = 8
const SLOT_DURATION_S = 3
const DEFAULT_VARIANT_INDEX = 1
// V3.4 widens the arc to 7 cards; V3.5 tightens it to 3.
const WIDE_ARC_VARIANT = 4
const TIGHT_ARC_VARIANT = 5
const WIDE_ARC_MAX_CARDS = 7
const TIGHT_ARC_MAX_CARDS = 3
// V3.6 (fast) and V3.7 (slow) scale the cycle duration. Implemented in
// TS rather than CSS because the duration is set inline (per-tile) from
// the array length × SLOT_DURATION_S — overriding via SCSS would require
// `!important` which the codebase forbids.
const FAST_CYCLE_VARIANT = 6
const SLOW_CYCLE_VARIANT = 7
const FAST_CYCLE_MULTIPLIER = 0.5
const SLOW_CYCLE_MULTIPLIER = 1.67

function durationMultiplier (variantIndex: number): number {
  if (variantIndex === FAST_CYCLE_VARIANT) return FAST_CYCLE_MULTIPLIER
  if (variantIndex === SLOW_CYCLE_VARIANT) return SLOW_CYCLE_MULTIPLIER
  return 1
}

interface CoverFlowProps {
  thumbnails: string[]
  variantIndex: number
}

/**
 * Some variants override the visible card count. V3.4 caps at 7 cards;
 * V3.5 caps at 3 cards (front + 2 sides). Other variants use the full
 * thumbnail array unchanged.
 */
function clampForVariant (thumbnails: string[], variantIndex: number): string[] {
  if (variantIndex === WIDE_ARC_VARIANT) {
    return thumbnails.slice(0, WIDE_ARC_MAX_CARDS)
  }
  if (variantIndex === TIGHT_ARC_VARIANT) {
    return thumbnails.slice(0, TIGHT_ARC_MAX_CARDS)
  }
  return thumbnails
}

/**
 * Resolve which keyframe-N variant to use. The SCSS file ships
 * pre-generated keyframes for N = 1..8; arrays longer than 8 reuse the
 * N=8 cycle (their extra cards still take turns at the front, just
 * with the visual layout of an 8-slot arc).
 */
function resolveKeyframeN (count: number): number {
  if (count <= 1) return 1
  if (count >= MAX_KEYFRAME_N) return MAX_KEYFRAME_N
  return count
}

function CoverFlow ({ thumbnails, variantIndex }: CoverFlowProps): React.ReactElement {
  const variantClass = `playlist-search-tile-v3--variant-${variantIndex}`

  if (thumbnails.length === 0) {
    return (
      <div className={`playlist-search-tile-v3__placeholder ${variantClass}`}>
        <StackedCards size="40px" color="rgba(255, 255, 255, 0.65)" />
      </div>
    )
  }

  const visibleThumbs = clampForVariant(thumbnails, variantIndex)

  if (visibleThumbs.length === 1) {
    return (
      <div className={`playlist-search-tile-v3__stage ${variantClass}`}>
        <div className="playlist-search-tile-v3__card playlist-search-tile-v3__card--static">
          <img
            src={visibleThumbs[0]}
            alt=""
            loading="lazy"
            className="playlist-search-tile-v3__img"
          />
        </div>
      </div>
    )
  }

  const n = visibleThumbs.length
  const keyframeN = resolveKeyframeN(n)
  const cycleDuration = keyframeN * SLOT_DURATION_S * durationMultiplier(variantIndex)

  return (
    <div
      className={`playlist-search-tile-v3__stage playlist-search-tile-v3__stage--n${keyframeN} ${variantClass}`}
    >
      {visibleThumbs.map((url, i) => {
        // Distribute starting positions evenly across the cycle so the
        // set as a whole rotates: card i starts (i * SLOT_DURATION_S)
        // seconds into the keyframe via a negative animation-delay.
        const delay = -((i % keyframeN) * SLOT_DURATION_S)
        return (
          <div
            key={`${url}-${i}`}
            className="playlist-search-tile-v3__card"
            style={{
              animationDelay: `${delay}s`,
              animationDuration: `${cycleDuration}s`,
            }}
          >
            <img
              src={url}
              alt=""
              loading="lazy"
              className="playlist-search-tile-v3__img"
            />
          </div>
        )
      })}
    </div>
  )
}

interface PlaylistSearchTileV3Props extends PlaylistSearchTileProps {
  /** Animation variant 1..10 for side-by-side comparison. Defaults to 1
   * (the original cover-flow behavior). See `_PlaylistSearchTileV3.scss`
   * for the per-variant overrides. */
  variantIndex?: number
}

function PlaylistSearchTileV3 ({
  variantIndex = DEFAULT_VARIANT_INDEX,
  ...props
}: PlaylistSearchTileV3Props): React.ReactElement {
  return (
    <PlaylistTileShell
      {...props}
      renderHero={({ previewThumbnailUrls }) => (
        <CoverFlow
          thumbnails={previewThumbnailUrls}
          variantIndex={variantIndex}
        />
      )}
      renderHoverBody={({
        playlistName,
        metaLine,
        previewThumbnailUrls,
        previewContentTitles,
        itemCount,
      }) => (
        <PlaylistTileHoverBodyV3
          playlistName={playlistName}
          metaLine={metaLine}
          thumbnails={previewThumbnailUrls}
          titles={previewContentTitles}
          itemCount={itemCount}
        />
      )}
    />
  )
}

export default React.memo(PlaylistSearchTileV3)
